import { UtilService } from 'src/app/core/services/util/util.service';

import { ElementRef, EventEmitter, Injectable, OnDestroy, DOCUMENT, inject } from '@angular/core';
import { Router } from '@angular/router';
import { asyncScheduler, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, share, throttleTime } from 'rxjs/operators';
import { AppGlobalsModule as _globals } from './app-globals.module';
import { GoogleTextAdService } from './content/google-text-ad/google-text-ad.service';
import { BreakPointSizeService } from './core/services/break-point-size/break-point-size.service';
import { BrowserPlatformService } from './core/services/browser-platform/browser-platform.service';
import { HistoryRouteService } from './core/services/history-route/history-route.service';

export enum DirectionScrolling {
    Up = 'up',
    Down = 'down',
}

@Injectable({
    providedIn: 'root',
})
export class ScrollingService implements OnDestroy {
    private _historyRoute = inject(HistoryRouteService);
    private _breakPointSizeService = inject(BreakPointSizeService);
    private adService = inject(GoogleTextAdService);
    private _doc = inject<Document>(DOCUMENT);
    private _utils = inject(UtilService);
    private _browser = inject(BrowserPlatformService);

    readonly DEFAULT_TOP_RANGE: number = 25;
    public adTopHeight!: number;
    public readyToScroll = true;
    public scrollToAppTop: EventEmitter<boolean> = new EventEmitter(true);
    public lastScrolledPage = '';
    private _topRange: number = this.DEFAULT_TOP_RANGE;
    subscribes: Subscription[] = [];
    constructor() {
        this.subscribes.push(
            this.adService.getIsAdTopEnable().subscribe((_isAdTopEnable: boolean) => {
                let _isAdTopLoadedSubscription: Subscription | undefined;
                if (_isAdTopEnable) {
                    _isAdTopLoadedSubscription = this.adService.getIsAdTopLoaded().subscribe((isAdTopLoaded: boolean) => {
                        if (isAdTopLoaded) {
                            this._topRange = this.adTopHeight;
                            if (_isAdTopLoadedSubscription) {
                                _isAdTopLoadedSubscription.unsubscribe();
                            }
                        } else {
                            this._topRange = this.DEFAULT_TOP_RANGE;
                        }
                    });
                    this.subscribes.push(_isAdTopLoadedSubscription);
                } else {
                    if (_isAdTopLoadedSubscription) {
                        _isAdTopLoadedSubscription.unsubscribe();
                    }
                    this._topRange = this.DEFAULT_TOP_RANGE;
                }
            }),
        );
        this.subscribes.push(
            this.adService.getCurrAdTopType().subscribe((currAdTop) => {
                this.adTopHeight = this.adService.getAdDimensions(currAdTop).height;
            }),
        );
    }

    // This would be edited in order to support multi-headers.

    // Observables
    public readonly scroll$ = this._browser.fromWindowEvent('scroll').pipe(
        throttleTime(25, asyncScheduler, { leading: true, trailing: true }),
        map(() => this._browser.pageYOffset),
        share(),
    );

    public readonly scrollDirection$ = this.scroll$.pipe(
        pairwise(),
        map(([y1, y2]): DirectionScrolling => (y2 < y1 ? DirectionScrolling.Up : DirectionScrolling.Down)),
        distinctUntilChanged(),
        share(),
    );

    public readonly scrollUp$ = this.scrollDirection$.pipe(
        throttleTime(50, asyncScheduler, { leading: true, trailing: true }),
        filter((direction) => direction === DirectionScrolling.Up),
    );

    public readonly scrollDown$ = this.scrollDirection$.pipe(
        throttleTime(50, asyncScheduler, { leading: true, trailing: true }),
        filter((direction) => direction === DirectionScrolling.Down),
    );

    public readonly scrollAtTop$ = this.scroll$.pipe(
        map((offsetY: number): boolean => offsetY <= this._topRange),
        distinctUntilChanged(),
    );

    /**
     * Indicate initiation original screen visibality,
     *  will change to false once scroll out of the initial view
     */
    public readonly isFirstScreen$ = this.scroll$.pipe(debounceTime(1000)).pipe(
        map((offsetY: number): boolean => offsetY <= this._browser.viewportHeight),
        distinctUntilChanged(),
    );

    public readonly contentBodyEl$ = this.scroll$
        // .pipe(debounceTime(50))
        .pipe(
            throttleTime(100, asyncScheduler, { leading: true, trailing: false }),
            map((offsetY: number): ContentBodyPosition => this.getContentBodyScrollY(offsetY), distinctUntilChanged()),
        );

    private _contentBodyEl!: ElementRef;

    private _contentBodyEl$ = new Subject<ElementRef>();
    public setContentBodyEl(contentBodyEl: ElementRef) {
        this._contentBodyEl = contentBodyEl;
        this._contentBodyEl$.next(contentBodyEl);
    }

    private getContentBodyScrollY(offsetYWindow: number): ContentBodyPosition {
        if (this._contentBodyEl) {
            const { offsetHeight } = this._contentBodyEl.nativeElement;
            const elemRect = this._contentBodyEl.nativeElement.getBoundingClientRect();
            const offset = elemRect.top - this._doc.body.getBoundingClientRect().top;
            const offsetY = Math.ceil(this._browser.viewportHeight / 2 + offsetYWindow - offset);
            return {
                offsetHeight,
                offsetY,
                percentageY: Math.ceil((offsetY / offsetHeight) * 100),
            };
        } else {
            return {
                offsetHeight: 0,
                offsetY: 0,
                percentageY: 0,
            };
        }
    }

    getScrollBegin() {
        let offsetFromTop = this._doc.getElementsByTagName('header')[0].getBoundingClientRect().top + this._browser.scrollY;
        let topAppPlace = this._doc.getElementsByTagName('header')[0].offsetHeight + offsetFromTop;
        return topAppPlace - offsetFromTop;
    }

    getMainElement() {
        return this._doc.getElementsByClassName('app-body')[0];
    }

    getDistanceFromTop() {
        try {
            return this.getMainElement().scrollHeight + this.getScrollBegin();
        } catch (error) {
            // console.log('dist error');
            return this.getScrollBegin();
        }
    }

    async HandleScroll(router: Router) {
        this.readyToScroll = false;
        // console.log('handleScroll url: ' + this.getCurrentUrl(router));//we need to not use this scroll to be tracked
        let hgt = this.getScrollBegin();
        let key = this.getScrollPositionKey();
        let prevTopRaw = this._browser.sessionStorage?.getItem(key);
        let doScroll =
            prevTopRaw &&
            (this._historyRoute.isPopBackNavigation || this._historyRoute.isPopForwardNavigation) &&
            !/(ask|midrash)$/i.test(this.getCurrentUrl(router));
        let itterations = 0;
        let itterationsMax = 2;
        if (doScroll) {
            let splt = prevTopRaw?.split(',');
            if (splt && splt.length >= 3) {
                let loc = parseInt(splt[1], 10);
                let prevHeight = parseInt(splt[2], 10);
                if (this._browser.isBrowser) {
                    while (itterations < itterationsMax && this.getDistanceFromTop() !== prevHeight) {
                        itterations++;
                        await this.delay(500);
                    }
                    hgt = this.getDistanceFromTop() === prevHeight ? loc : this.getDistanceFromTop() - this._browser.outerHeight;
                }
            }
        } else {
            this.scrollToAppTop.emit();
            this.readyToScroll = true;
        }
        // Use 'auto' for broader Android WebView compatibility
        this._browser.scrollTo({ top: hgt, behavior: 'smooth' });
        itterations = 0;
        if (this._browser.isBrowser) {
            while (this._browser.scrollY !== hgt && itterations < itterationsMax) {
                await this.delay(100);
            }
        }
        this.readyToScroll = true;
    }

    getCurrentUrl(router: Router): string {
        return this.lastScrolledPage ? this.lastScrolledPage : router.url;
    }

    delay(ms: number): Promise<void> {
        return new Promise<void>((resolve) => {
            // Use custom setTimeout to avoid issues with server-side rendering
            if (_globals.setTimeoutIsNotServer(resolve, ms <= 0 ? -1 : ms) === null) {
                resolve();
            }
        });
    }

    getScrollPosition() {
        if (this._browser.isBrowser) {
            return [this._browser.pageXOffset, this._browser.pageYOffset];
        }
        let sx;
        let sy;
        let d = this._doc;
        let r = d.documentElement;
        let b = d.body;
        sx = r.scrollLeft || b.scrollLeft || 0;
        sy = r.scrollTop || b.scrollTop || 0;
        return [sx, sy];
    }

    getScrollPositionKey() {
        let key = `pagePos-url:${this._historyRoute.navigationId}-mobile:${this._breakPointSizeService.isMobileSize()}`;
        return key;
    }

    setScrollLocation(router: Router) {
        const storage = this._browser.sessionStorage;
        if (this.readyToScroll && storage) {
            storage.setItem(this.getScrollPositionKey(), `${this._browser.pageXOffset},${this._browser.pageYOffset},${this.getDistanceFromTop()}`);
        }
    }

    ngOnDestroy(): void {
        if (this.scrollToAppTop) {
            this.scrollToAppTop.unsubscribe();
        }
        if (this._contentBodyEl$) {
            this._contentBodyEl$.unsubscribe();
        }
        this._utils.unsubscribe(this.subscribes);
    }
}

export interface ContentBodyPosition {
    offsetHeight: number;
    offsetY: number;
    percentageY: number;
}
